import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase';
import toast from 'react-hot-toast';

const FUNCTIONS_BASE_URL = 'https://us-central1-ezypipe.cloudfunctions.net';

/**
 * Custom hook for Google (Gmail & Calendar) OAuth integration
 * Provides real-time connection status and connect/disconnect functions
 */
const useGmailAuth = (currentUser) => {
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState(null);
  const [gmailSyncEnabled, setGmailSyncEnabled] = useState(false);
  const [calendarSyncEnabled, setCalendarSyncEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  // Listen to user document for real-time Google connection status
  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, 'users', currentUser.uid);

    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          setGoogleConnected(userData.googleConnected || false);
          setGoogleEmail(userData.googleEmail || null);
          setGmailSyncEnabled(userData.gmailSyncEnabled || false);
          setCalendarSyncEnabled(userData.calendarSyncEnabled || false);
        } else {
          setGoogleConnected(false);
          setGoogleEmail(null);
          setGmailSyncEnabled(false);
          setCalendarSyncEnabled(false);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to user document:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [currentUser?.uid]);

  // Check for OAuth callback success and register Calendar watch
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleConnectedParam = urlParams.get('google_connected');
    const errorParam = urlParams.get('google_error');

    if (googleConnectedParam === 'true') {
      toast.success('Gmail & Calendar connected successfully!');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Register Calendar watch channel for push notifications
      registerCalendarWatch();
    } else if (errorParam === 'true') {
      toast.error('Failed to connect Google account. Please try again.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  /**
   * Register Calendar watch channel for push notifications
   * This enables automatic sync when calendar events are created/modified
   */
  const registerCalendarWatch = async () => {
    try {
      const calendarRegisterWatch = httpsCallable(functions, 'calendarRegisterWatch');
      const result = await calendarRegisterWatch({});
      console.log('[useGmailAuth] Calendar watch registered:', result.data);
    } catch (err) {
      // Don't show error to user - Calendar watch is optional enhancement
      console.error('[useGmailAuth] Error registering calendar watch:', err);
    }
  };

  /**
   * Initiates Google (Gmail & Calendar) OAuth flow
   * Redirects user to Google consent screen
   */
  const connectGoogle = async () => {
    if (!auth.currentUser) {
      toast.error('Please log in first');
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      // Get Firebase ID token
      const idToken = await auth.currentUser.getIdToken();

      // Call gmailAuthStart Cloud Function
      const response = await fetch(`${FUNCTIONS_BASE_URL}/gmailAuthStart`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start OAuth flow');
      }

      if (!data.authUrl) {
        throw new Error('No authorization URL received');
      }

      // Redirect to Google OAuth consent screen
      window.location.href = data.authUrl;
    } catch (err) {
      console.error('Error connecting Google account:', err);
      setError(err.message);
      toast.error('Failed to connect Google account. Please try again.');
      setConnecting(false);
    }
  };

  /**
   * Disconnects Google account (Gmail & Calendar)
   * Removes stored tokens and updates user document
   */
  const disconnectGoogle = async () => {
    if (!auth.currentUser) {
      toast.error('Please log in first');
      return;
    }

    setDisconnecting(true);
    setError(null);

    try {
      // Stop Calendar watch channel first
      try {
        const calendarStopWatch = httpsCallable(functions, 'calendarStopWatch');
        await calendarStopWatch({});
        console.log('[useGmailAuth] Calendar watch stopped');
      } catch (watchErr) {
        console.error('[useGmailAuth] Error stopping calendar watch:', watchErr);
        // Continue with disconnect even if watch stop fails
      }

      // Get Firebase ID token
      const idToken = await auth.currentUser.getIdToken();

      // Call gmailAuthDisconnect Cloud Function
      const response = await fetch(`${FUNCTIONS_BASE_URL}/gmailAuthDisconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to disconnect Google account');
      }

      toast.success('Google account disconnected successfully');

      // Sync with extension
      const { syncSettingsWithExtension } = await import('../utils/extensionSync');
      await syncSettingsWithExtension();
    } catch (err) {
      console.error('Error disconnecting Google account:', err);
      setError(err.message);
      toast.error('Failed to disconnect Google account. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  return {
    googleConnected,
    googleEmail,
    gmailSyncEnabled,
    calendarSyncEnabled,
    loading,
    connecting,
    disconnecting,
    error,
    connectGoogle,
    disconnectGoogle,
  };
};

export default useGmailAuth;
